/**
 * A key signature moved with a transposing instrument.
 *
 * The document keeps one key per bar for the whole piece, at concert pitch, and every part
 * keeps the pitches its player reads. Those two facts only agree on the page if the key
 * signature is moved on the way out: a clarinet in B flat reading in concert C major has
 * two sharps printed in front of it, and a horn in F has one. Printing the concert
 * signature over written notes gives a staff nobody can read -- the notes are right and
 * every accidental in the bar is spelled against the wrong scale.
 *
 * The shift is counted in fifths, which is what a signature is. Moving up a semitone moves
 * the signature seven fifths, so the whole table falls out of the transposition's own
 * semitone count; the octave part of a transposition is left out of it, because a piccolo
 * and a double bass read the signature everyone else does.
 *
 * Directions are named rather than signed, as they are for pitch: {@link toWrittenKey}
 * takes the piece's key to the one a part prints, {@link toSoundingKey} brings a part's
 * printed key back to the piece's.
 */

import type { Instrument, KeySignature } from "./types";
import type { Transposition } from "./pitch";

/** Fifths in an octave: the length of the circle. */
const CIRCLE = 12;

/** Fifths per semitone, going up. Seven fifths up is one semitone up, twelve octaves later. */
const FIFTHS_PER_SEMITONE = 7;

/** The furthest a printed signature is allowed to go before it is respelled. */
const LIMIT = 7;

/**
 * How many fifths this transposition moves a signature, written towards sounding.
 *
 * The answer is the smallest of the enharmonic possibilities: an instrument in E flat
 * moves the signature three flats rather than nine sharps, which are the same key and only
 * one of which is ever printed.
 */
export function keyShift(transposition: Transposition): number {
  const raw = (((transposition.chromatic * FIFTHS_PER_SEMITONE) % CIRCLE) + CIRCLE) % CIRCLE;
  return raw > CIRCLE / 2 ? raw - CIRCLE : raw;
}

/**
 * The same signature spelled with as few accidentals as it can be.
 *
 * Seven sharps and five flats are the same key, and a part is printed in whichever of them
 * is shorter to write: transposing a piece in E major for an instrument in E flat gives
 * seven sharps by arithmetic and five flats on the page. Beyond seven the respelling is not
 * a preference but a necessity -- there is no eighth sharp to print.
 */
function respelled(fifths: number): number {
  let value = fifths;
  while (value > LIMIT) value -= CIRCLE;
  while (value < -LIMIT) value += CIRCLE;
  const other = value > 0 ? value - CIRCLE : value + CIRCLE;
  return Math.abs(other) < Math.abs(value) && Math.abs(other) <= LIMIT ? other : value;
}

function moved(key: KeySignature, fifths: number): KeySignature {
  return fifths === 0 ? key : { ...key, fifths: respelled(key.fifths + fifths) };
}

/** The signature a transposing part prints, given the piece's own key. */
export function toWrittenKey(sounding: KeySignature, transposition: Transposition): KeySignature {
  return moved(sounding, -keyShift(transposition));
}

/** The piece's key, given the signature a transposing part prints. */
export function toSoundingKey(written: KeySignature, transposition: Transposition): KeySignature {
  return moved(written, keyShift(transposition));
}

/**
 * The other spelling of a signature, where it has one, or the same one where it has not.
 *
 * Five sharps and seven flats are the same key; so are six of each. Above seven of either
 * there is no signature to print at all, which is why {@link respelled} keeps every
 * derived key inside that bound and why this can only ever move a key by the whole circle.
 */
function enharmonic(key: KeySignature, side: "sharps" | "flats"): KeySignature {
  const moved = side === "sharps" ? key.fifths + CIRCLE : key.fifths - CIRCLE;
  const wanted = side === "sharps" ? key.fifths < 0 : key.fifths > 0;
  return wanted && Math.abs(moved) <= LIMIT ? { ...key, fifths: moved } : key;
}

/**
 * The signature a part prints for a key the piece is in.
 *
 * Most parts print the piece's key moved by their own transposition. The ones written
 * without a signature print an empty one instead -- not nothing, because a part that
 * states no signature at all is a part whose signature the reader has to guess, and
 * MusicXML has a way of saying "none" that round trips.
 */
export function partKey(sounding: KeySignature, instrument: Instrument): KeySignature {
  const printing = instrument.keyPrinting;
  if (printing === "none") return { fifths: 0, mode: null };
  const written = toWrittenKey(sounding, instrument.transposition);
  return printing === "derived" ? written : enharmonic(written, printing);
}
